import {
  authApi,
  clearStoredAuthTokens,
  type AuthSessionEnvelope
} from "./auth-api";
import {
  clearSession,
  getSession,
  saveSession,
  updateSession,
  type LocalSession
} from "./session";

const SESSION_REVALIDATE_TTL_MS = 30_000;

let lastHydratedAt = 0;
let inFlightSessionHydration: Promise<LocalSession | null> | null = null;

function markSessionHydratedNow(): void {
  lastHydratedAt = Date.now();
}

function shouldRevalidateSessionNow(): boolean {
  return Date.now() - lastHydratedAt > SESSION_REVALIDATE_TTL_MS;
}

export function requireSession(): LocalSession {
  const session = getSession();
  if (!session) {
    throw new Error("Not authenticated");
  }
  return session;
}

export function persistAuthSession(input: AuthSessionEnvelope): LocalSession {
  const session: LocalSession = {
    userId: input.user.id,
    email: input.user.email,
    name: input.user.name,
    emailVerified: input.user.emailVerified,
    image: input.user.image ?? null,
    orgId: input.organizationId ?? "",
    workspaceId: input.workspaceId ?? undefined,
    workspaceName: input.workspaceName ?? undefined,
    role: input.role ?? "member",
    plan: input.plan ?? "starter",
    effectivePlan: input.effectivePlan ?? input.plan ?? "starter",
    isActive: input.isActive ?? true,
    isReadOnly: input.isReadOnly ?? false,
    canUsePaidMessaging: input.canUsePaidMessaging ?? false,
    status: input.status ?? "inactive",
    accessLevel: input.accessLevel ?? "read_only",
    trialEndsAt: input.trialEndsAt ?? null,
    trialState: input.trialState ?? "none",
    currentProjectId: input.projectId ?? undefined,
    currentProjectPlatform: input.projectPlatform ?? undefined,
    onboardingComplete: Boolean(input.onboardingComplete),
    onboardingStep: input.onboardingStep ?? 1
  };

  saveSession(session);
  markSessionHydratedNow();
  return session;
}

export function setOnboardingComplete(projectId?: string): void {
  updateSession({
    onboardingComplete: true,
    onboardingStep: 4,
    currentProjectId: projectId ?? getSession()?.currentProjectId
  });
  markSessionHydratedNow();
}

export async function signOut(): Promise<void> {
  clearStoredAuthTokens();
  clearSession();
  lastHydratedAt = 0;
  inFlightSessionHydration = null;

  try {
    await authApi.logout();
  } catch (error) {
    console.warn("Logout revoke request failed after local sign-out", error);
  }
}

export async function hydrateSessionFromApi(options?: {
  allowRefresh?: boolean;
}): Promise<LocalSession | null> {
  const direct = await authApi.getCurrentSession();

  if (direct.data?.user) {
    return persistAuthSession(direct.data);
  }

  const shouldRefresh = (options?.allowRefresh ?? true) && direct.status === 401;
  if (!shouldRefresh) {
    if (direct.status === 401) {
      clearStoredAuthTokens();
      clearSession();
    }
    return null;
  }

  const refresh = await authApi.refreshSession();
  if (!refresh.data?.user) {
    if (refresh.status === 401) {
      clearStoredAuthTokens();
      clearSession();
    }
    return null;
  }

  return persistAuthSession(refresh.data);
}

export async function switchWorkspace(workspaceId: string): Promise<LocalSession> {
  const response = await authApi.switchWorkspace({ workspaceId });
  if (!response.data?.user) {
    throw new Error(response.error || "Failed to switch workspace.");
  }
  return persistAuthSession(response.data);
}

export async function ensureSession(): Promise<LocalSession | null> {
  const localSession = getSession();
  if (localSession && !shouldRevalidateSessionNow()) {
    return localSession;
  }

  if (!inFlightSessionHydration) {
    inFlightSessionHydration = hydrateSessionFromApi({ allowRefresh: true })
      .then((hydrated) => {
        markSessionHydratedNow();
        if (hydrated) {
          return hydrated;
        }
        return getSession();
      })
      .finally(() => {
        inFlightSessionHydration = null;
      });
  }

  if (localSession) {
    void inFlightSessionHydration.catch(() => null);
    return localSession;
  }

  return inFlightSessionHydration;
}
